import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { api } from '../../api';
import './index.module.css'


// Hàm định dạng ngày theo DD/MM/YYYY HH:mm
const formatDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

// Cấu trúc cột của bảng
const columns = [
    {
        accessorKey: "index",
        header: "STT",
        cell: ({ row }) => row.index + 1, // Tạo STT tự động từ 1, 2, 3...
    },
    { accessorKey: 'user_name', header: 'Tên người quay' },
    { accessorKey: 'prize_name', header: 'Giải thưởng' },
    { accessorKey: 'phone', header: 'Điện thoại' },
    { accessorKey: 'address', header: 'Địa chỉ' },
    { accessorKey: 'branch', header: 'Cơ sở mua hàng' },
    { accessorKey: 'purchased_vehicle', header: 'Phương tiện đã mua' },
    {
        accessorKey: 'spin_time',
        header: 'Thời gian đã quay',
        cell: ({ getValue }) => formatDate(getValue()), // Format ngày
    },
];

const WinnerList = () => {
    // Mặc định ngày bắt đầu là 30 ngày trước
    const defaultStartDate = new Date();
    defaultStartDate.setDate(defaultStartDate.getDate() - 30);
    const defaultEndDate = new Date();
    defaultEndDate.setDate(defaultEndDate.getDate() + 1); // Cộng thêm 1 ngày để thành ngày mai
    defaultEndDate.setHours(23, 59, 59, 999); // Đặt giờ đến 23:59:59

    const [startDate, setStartDate] = useState(defaultStartDate.toISOString().split("T")[0]);
    const [endDate, setEndDate] = useState(defaultEndDate.toISOString().split("T")[0]); // Chỉ hiển thị phần ngày
    const [filterName, setFilterName] = useState("");

    const [data, setData] = useState([]);

    const getData = useCallback(async () => {
        try {
            const response = await api.get('/winners', {
                params: {
                    from_date: startDate,  // Ngày bắt đầu
                    to_date: endDate,  // Ngày kết thúc đến 23:59
                },
            });
            if (response?.data) {
                setData(response.data);
            } else {
                alert('Có lỗi xảy ra khi lấy dữ liệu.');
            }
        } catch (error) {
            alert('Lỗi khi tải danh sách trúng thưởng');
            console.error(error);
        }
    }, [startDate, endDate]);


    useEffect(() => {
        getData();
    }, [startDate, endDate]);

    // Lọc dữ liệu theo tên và khoảng thời gian
    const filteredData = useMemo(() => {
        return data?.filter((row) => {
            const searchText = filterName.toLowerCase(); // Chuyển text tìm kiếm thành chữ thường

            // Nếu không nhập từ khóa, hiển thị tất cả dữ liệu
            const matchKeyword = filterName
                ? Object.values(row).some(value =>
                    value?.toString().toLowerCase().includes(searchText)
                )
                : true;

            // Chuyển đổi ngày về kiểu Date để so sánh
            const spinDate = new Date(row.spin_time);
            const start = startDate ? new Date(startDate) : null;

            const end = endDate ? new Date(endDate) : null;;

            const matchDate =
                (!start || spinDate >= start) && // Ngày lớn hơn hoặc bằng ngày bắt đầu
                (!end || spinDate <= end); // Ngày nhỏ hơn hoặc bằng ngày kết thúc

            return matchKeyword && matchDate;
        });
    }, [data, filterName, startDate, endDate]);


    const table = useReactTable({
        data: filteredData,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const exportToExcel = useCallback(async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Danh sách trúng thưởng");

        // ✅ Chính xác hóa tiêu đề tổng số người trúng thưởng
        const title = `Tổng số người trúng thưởng: (${filteredData.length} người) từ ngày ${formatDate(startDate)} đến ngày ${formatDate(endDate)}`;

        // 🎯 Gộp ô tiêu đề linh hoạt dựa trên số cột
        const totalColumns = columns.length; // Tổng số cột
        const lastColumnLetter = String.fromCharCode(64 + totalColumns); // Chuyển số cột thành chữ cái (A, B, C...)

        worksheet.mergeCells(`A1:${lastColumnLetter}1`); // Gộp ô đúng phạm vi

        worksheet.getCell("A1").font = { bold: true, size: 16 };
        worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };

        // 📝 Lấy tiêu đề cột từ bảng (STT là cột đầu tiên)
        const tableHeaders = columns.map(col => col.header);

        // 📌 Ghi tiêu đề vào hàng thứ 2
        worksheet.addRow(tableHeaders);
        const headerRow = worksheet.getRow(2);
        headerRow.font = { bold: true, size: 12 };
        headerRow.alignment = { horizontal: "center", vertical: "middle" };

        // 🎯 Thêm border cho tiêu đề cột
        headerRow.eachCell(cell => {
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
        });

        // 🏗 Thêm dữ liệu vào bảng, STT tự động tăng từ 1
        filteredData.forEach((row, index) => {
            const rowData = columns.map(col => {
                if (col.accessorKey === "index") return index + 1;
                if (col.accessorKey === "spin_time") return formatDate(row[col.accessorKey]); // Format ngày tháng
                return row[col.accessorKey] || "";
            });

            worksheet.addRow(rowData);
        });

        // 🎯 Căn giữa + Thêm border cho tất cả ô dữ liệu
        worksheet.eachRow((row, rowNumber) => {
            row.eachCell(cell => {
                cell.alignment = { horizontal: "center", vertical: "middle" };
                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                };
            });

            // Định dạng chữ to + in đậm cho header
            if (rowNumber === 2) {
                row.font = { bold: true, size: 12 };
            }
        });

        // 📏 Thiết lập chiều rộng cột
        worksheet.columns = columns.map(col => ({
            header: col.header,
            key: col.accessorKey,
            width: 20
        }));

        worksheet.getCell("A1").value = title;


        // 📂 Xuất file
        const buffer = await workbook.xlsx.writeBuffer();
        const fileName = `DS_Trung_Thuong_${formatDate(startDate)}_${formatDate(endDate)}.xlsx`;
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName);
    }, [filteredData, startDate, endDate]);



    return (
        <div className="container">
            <h2>
                Tổng số người trúng thưởng: ({filteredData.length} người) từ ngày {formatDate(startDate)} đến ngày {formatDate(new Date())}
            </h2>

            {/* Ô tìm kiếm và bộ lọc */}
            <div style={{ marginBottom: "10px", display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                    type="text"
                    placeholder="Tìm theo ..."
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                />

                <label>
                    Từ ngày:
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </label>

                <label>
                    Đến ngày:
                    <input
                        type="date"
                        value={endDate.split("T")[0]} // Hiển thị ngày, nhưng lưu full datetime
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </label>

                <button onClick={exportToExcel}>Xuất Excel</button>
            </div>

            {/* Bảng hiển thị danh sách */}
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((column) => (
                                <th key={column.id} style={{ border: "1px solid black", padding: "5px", color: 'black' }}>
                                    {flexRender(column.column.columnDef.header, column.getContext())}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                                <td key={cell.id} style={{ border: "1px solid black", padding: "5px" }}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default WinnerList;
